import { breakpointsTailwind, useBreakpoints, useScrollLock } from "@vueuse/core";
import type { AxiosError } from "axios";
import { isRef, onBeforeUnmount, ref, shallowReadonly } from "vue";
import type { MaybeRef, Ref, UnwrapRef } from "vue";

/**
 * @param initialValue The initial value which can be ref or not
 * @returns A readonly value and a setter function for it
 */
export const useReadonlyWithSetter = <IV>(initialValue: MaybeRef<IV>) => {
  const valueRef = isRef(initialValue) ? initialValue : ref(initialValue);
  const readonlyValue = shallowReadonly(valueRef as Ref<IV>);

  function setValue(value: IV) {
    valueRef.value = value as UnwrapRef<IV>;
  }

  return [readonlyValue, setValue] as const;
};

interface PromiseWrapperOptions {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onError?: <E extends AxiosError<any>>(error: E) => void;
  finally?: () => void;
}

/**
 * Promise Wrapper for async calls to the API
 *
 * @param callback The promiseable callback
 * @returns Wrapper containing common values and helpers
 */
export const usePromiseWrapper = <
  // Added any here because the callback is too generic
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  TFunc extends (...params: Array<any>) => ReturnType<TFunc>,
>(
  callback: TFunc,
  options?: PromiseWrapperOptions
) => {
  const loading = ref(false);
  const error = ref<unknown | null>(null);

  const executeFetch = async (...params: Parameters<TFunc>) => {
    try {
      loading.value = true;
      error.value = null;
      return await callback(...params);
    } catch (caughtError) {
      console.error(caughtError);
      error.value = caughtError;
      if (options?.onError) {
        options.onError(caughtError as AxiosError);
      }
    } finally {
      loading.value = false;
      if (options?.finally) {
        options.finally();
      }
    }
  };

  return { loading, error, executeFetch };
};

/**
 * A set of helpers for responsive design
 */
export const useResponsive = () => {
  const { smallerOrEqual } = useBreakpoints(breakpointsTailwind);

  const isMobile = smallerOrEqual("sm");

  return { isMobile };
};

export const useLockDocumentScrollWhileComponentExists = () => {
  const isLocked = useScrollLock(document.body, true);

  onBeforeUnmount(() => {
    isLocked.value = false;
  });
};
